/**
 * End of speech from the microphone level (docs/partial/shopping-in-store-spec.md 6.1) – used
 * where the recogniser gives no text while recording. The level never leaves the device.
 *
 * The noise floor is the quietest window of the first 300 ms and keeps following the quiet
 * windows afterwards, so a steady hum is not taken for speech. In a loud store the level may
 * never fall below the threshold: then silence ends nothing and the tap, the stop word and the
 * maximum duration remain.
 */
export const LEVEL_WINDOW_MS = 100;
const CALIBRATION_MS = 300;
const MIN_THRESHOLD = 0.012;
const THRESHOLD_FACTOR = 2.5;
const FLOOR_ADAPTION = 0.05;

export type SilenceVerdict =
  | { state: 'calibrating' | 'waiting' | 'speech' }
  /** Speech was heard and it is quiet now; `silentForMs` of the allowed silence have passed. */
  | { state: 'silence'; silentForMs: number }
  /** The allowed silence after speech has passed. */
  | { state: 'end' }
  /** Nothing was said at all within the grace period. */
  | { state: 'nothing' };

export class SilenceDetector {
  private startedAt: number | null = null;
  private floor = Number.POSITIVE_INFINITY;
  private heardSpeech = false;
  private quietSince: number | null = null;

  constructor(private readonly options: { silenceMs: number; initialSilenceMs: number }) {}

  /** The loudness a window must exceed to count as speech. */
  get threshold(): number {
    const floor = Number.isFinite(this.floor) ? this.floor : 0;
    return Math.max(MIN_THRESHOLD, floor * THRESHOLD_FACTOR);
  }

  feed(rms: number, nowMs: number): SilenceVerdict {
    this.startedAt ??= nowMs;
    const elapsed = nowMs - this.startedAt;
    if (elapsed < CALIBRATION_MS) {
      this.floor = Math.min(this.floor, rms);
      return { state: 'calibrating' };
    }
    if (rms > this.threshold) {
      this.heardSpeech = true;
      this.quietSince = null;
      return { state: 'speech' };
    }
    this.floor = Number.isFinite(this.floor)
      ? this.floor * (1 - FLOOR_ADAPTION) + rms * FLOOR_ADAPTION
      : rms;
    if (!this.heardSpeech)
      return elapsed >= this.options.initialSilenceMs ? { state: 'nothing' } : { state: 'waiting' };
    this.quietSince ??= nowMs;
    const silentForMs = nowMs - this.quietSince;
    return silentForMs >= this.options.silenceMs
      ? { state: 'end' }
      : { state: 'silence', silentForMs };
  }
}
